// auth/signInFailure.ts
//
// The sign-in failure card. `bootstrap()` in main.js mounts the app only once Entra has signed the
// user in, so a failure there leaves the preloading spinner in index.html turning forever and puts
// the reason in a console line that the next redirect erases. This renders the reason into #app
// instead. Vue is not mounted at that point, so the card is raw DOM, built from the composition the
// Poseidon error pages share (AccessDenied.vue in the template reference copy) and the component
// classes in assets/tailwind.css.
//
// It deliberately does not import from `@/auth/msal`: mock mode aliases that path to a stub with
// three exports, and reaching for a fourth would break `npm run dev:mock`. Clearing sessionStorage
// is the same thing by other means, since MSAL's cacheLocation is the only user of it.

export interface SignInErrorDetail {
    error: string;
    description: string | null;
    correlationId: string | null;
    subError: string | null;
}

// Thrown when Entra refused the sign-in, so the card can show what Entra said rather than the
// generic message of whichever exception happened to surface.
export class SignInFailedError extends Error {
    constructor(readonly detail: SignInErrorDetail) {
        super(detail.description || detail.error);
        this.name = 'SignInFailedError';
    }
}

const surfaceShadow = 'shadow-[0px_87px_24px_0px_rgba(120,149,206,0.00),0px_56px_22px_0px_rgba(120,149,206,0.01),0px_31px_19px_0px_rgba(120,149,206,0.03),0px_14px_14px_0px_rgba(120,149,206,0.04),0px_3px_8px_0px_rgba(120,149,206,0.06)]';
const frostedCard = `bg-white/64 dark:bg-surface-800 backdrop-blur-[90px] rounded-3xl ${surfaceShadow}`;
const ghostCard = `w-full h-full inset-0 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 dark:shadow-sm ${frostedCard}`;

function describe(error: unknown): string {
    if (error instanceof SignInFailedError) {
        return error.detail.description || error.detail.error;
    }
    if (error instanceof Error) {
        return error.message;
    }
    return 'Entra did not complete the sign-in.';
}

export function showSignInFailure(error: unknown): void {
    console.error('[msal] Signing in failed, the back office was not started', error);

    const host = document.getElementById('app');
    if (!host) {
        return;
    }

    host.innerHTML = `
        <section class="animate-fadein animate-duration-300 animate-ease-in relative min-h-screen flex items-center justify-center">
            <div class="landing-container relative z-10">
                <div class="relative max-w-[46rem] mx-auto">
                    <div class="${ghostCard} rotate-[4deg] lg:rotate-[7deg]"></div>
                    <div class="${ghostCard} -rotate-[4deg] lg:-rotate-[7deg]"></div>
                    <div class="flex flex-col items-center p-8 py-20 relative z-10 ${frostedCard}">
                        <img src="/layout/images/logo-dark.svg" alt="Zeres" class="w-56 h-auto dark:hidden" />
                        <img src="/layout/images/logo-white.svg" alt="Zeres" class="w-56 h-auto hidden dark:block" />
                        <h2 class="text-4xl lg:text-6xl font-semibold text-surface-950 dark:text-surface-0 mt-8 text-center">Sign-in failed</h2>
                        <p data-reason class="lg:text-xl text-surface-500 dark:text-white/64 mt-4 text-center"></p>
                        <p data-correlation class="text-sm text-surface-400 dark:text-white/40 mt-3 text-center"></p>
                        <button type="button" data-retry class="button-primary mt-8">Sign in again</button>
                    </div>
                </div>
            </div>
        </section>`;

    // textContent rather than markup: the reason is a server message, and a server message is not
    // something to hand to innerHTML however trustworthy its source.
    const reason = host.querySelector('[data-reason]');
    if (reason) {
        reason.textContent = describe(error);
    }

    // The error code and its sub-error are what identify the failure, and the description often
    // does not name them. They go on the card so a screenshot is enough to diagnose this, rather
    // than needing the console line to have been caught while it was on screen.
    const correlation = host.querySelector('[data-correlation]');
    if (correlation) {
        const detail = error instanceof SignInFailedError ? error.detail : null;
        const parts = [detail?.error, detail?.subError, detail?.correlationId && `correlation id ${detail.correlationId}`].filter(Boolean);
        if (parts.length > 0) {
            correlation.textContent = parts.join(' · ');
        } else {
            correlation.remove();
        }
    }

    host.querySelector('[data-retry]')?.addEventListener('click', () => {
        // MSAL caches the account in sessionStorage and nothing else in the app uses it, so this
        // drops the broken session and the next boot starts a fresh sign-in.
        sessionStorage.clear();
        window.location.assign('/');
    });
}
